import * as React from 'react';
import { hydrateRoot } from 'react-dom/client';
import type { z } from 'zod/v4';
import ReactQueryClientProvider from '@contexts/QueryClientContext';
import { ErrorBoundary } from 'react-error-boundary';
import HttpError from '@helper/httpError';
import { constructFullUrl } from '@helper/urlHelpers';
import { initPostHog } from '@helper/posthog';
import { validateSsrProps } from '@helper/validateSsrProps';
import { hydrationScheduleFor, type HydrateOn } from './hydrationSchedule';
import { createHydrationQueue, HYDRATION_IMPORT_LIMIT } from './hydrationQueue';
import { scheduleIdleWork } from './scheduleIdleWork';
import { getListingFatShellModule } from './listingFatShellRegistry';

initPostHog();

export type HydrateLoadMode = 'dynamic' | 'listingFat';

export type SsrComponentModule = {
  default: React.ComponentType<Record<string, unknown>>;
  propsSchema?: z.ZodType;
};

type SsrComponentListItem = {
  domID: string;
  name: string;
  data: unknown;
  idx?: string | number;
  hydrationPriority?: number;
  hydrateOn?: HydrateOn;
};

const SCROLL_ROOT_MARGIN = '100% 0px';

const onError = (error: Error) => {
  if (error instanceof HttpError) {
    if (error.status === 401) {
      document.location.href = '/login/';
    }
    if (error.status === 401) {
      window.location.href = constructFullUrl('/logout/');
    }
  }
};

function ErrorRender() {
  return (
    <div>
      Something went wrong, please{' '}
      <a href="#" onClick={() => document.location.reload()}>
        try again
      </a>
      .
    </div>
  );
}

/**
 * Loads an SSR component module.
 * Listing-fat shell names come from the entry graph so Vite does not emit a second CSS chunk.
 *
 * @param name - SSR component name.
 * @param loadMode - `listingFat` may reuse a preloaded shell module.
 * @returns The component module.
 */
async function loadSsrComponent(
  name: string,
  loadMode: HydrateLoadMode,
): Promise<SsrComponentModule> {
  if (loadMode === 'listingFat') {
    const cached = getListingFatShellModule(name);
    if (cached) {
      return cached;
    }
  }
  return import(`../../components/${name}.tsx`) as Promise<SsrComponentModule>;
}

/**
 * Hydrates one SSR root after its module has loaded.
 *
 * @param item - One `SSR_COMPONENT_LIST` entry.
 * @param componentModule - Loaded component module.
 */
function hydrateLoadedComponent(
  item: SsrComponentListItem,
  componentModule: SsrComponentModule,
) {
  if (!componentModule?.default) {
    console.error(`${item.name} is not a valid element`);
    return;
  }

  validateSsrProps({
    name: item.name,
    idx: item.idx ?? item.domID,
    data: item.data,
    propsSchema: componentModule.propsSchema,
    surface: 'hydrate',
  });

  const element = document.getElementById(item.domID);
  if (!element) {
    console.error(`${item.domID} element is not present on the page`);
    return;
  }

  const Component = componentModule.default;
  const isDevelopment = process.env.NODE_ENV === 'development';
  const AppContent = (
    <ErrorBoundary onError={onError} fallbackRender={ErrorRender}>
      <ReactQueryClientProvider>
        <Component {...(item.data as Record<string, unknown>)} />
      </ReactQueryClientProvider>
    </ErrorBoundary>
  );

  hydrateRoot(
    element,
    isDevelopment ? <React.StrictMode>{AppContent}</React.StrictMode> : AppContent,
  );
}

/**
 * Reads schedule fields from the host list, falling back to the name map.
 *
 * @param item - Raw list entry. Older hosts omit the schedule fields.
 * @returns Item plus a concrete priority and trigger.
 */
function withSchedule(item: SsrComponentListItem): SsrComponentListItem & {
  hydrationPriority: number;
  hydrateOn: HydrateOn;
} {
  const schedule = hydrationScheduleFor(item.name);
  return {
    ...item,
    hydrationPriority: item.hydrationPriority ?? schedule.hydrationPriority,
    hydrateOn: item.hydrateOn ?? schedule.hydrateOn,
  };
}

/**
 * Enqueues scroll-triggered roots once they are within about one viewport.
 * Without IntersectionObserver they join the idle queue so they still hydrate.
 *
 * @param item - Component that waits for scroll.
 * @param enqueue - Queue insert used for idle items as well.
 */
function enqueueWhenNearViewport(
  item: SsrComponentListItem & { hydrationPriority: number },
  enqueue: () => void,
) {
  const element = document.getElementById(item.domID);
  if (!element || typeof IntersectionObserver === 'undefined') {
    enqueue();
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) {
      return;
    }
    observer.disconnect();
    enqueue();
  }, { rootMargin: SCROLL_ROOT_MARGIN });
  observer.observe(element);
}

/**
 * Hydrates `window.SSR_COMPONENT_LIST` by priority.
 * At most two modules load at once. Idle roots wait for browser idle, bounded by a timeout.
 * Scroll roots wait until they near the viewport.
 *
 * @param loadMode - `dynamic` uses the generic client entry; `listingFat` uses the fat entry.
 */
export function hydrateSsrComponentList(loadMode: HydrateLoadMode = 'dynamic') {
  const queue = createHydrationQueue(HYDRATION_IMPORT_LIMIT);
  const items = window.SSR_COMPONENT_LIST.map(withSchedule);

  const enqueue = (item: SsrComponentListItem & { hydrationPriority: number }) => {
    queue.enqueue({
      priority: item.hydrationPriority,
      run: async () => {
        const componentModule = await loadSsrComponent(item.name, loadMode);
        hydrateLoadedComponent(item, componentModule);
      },
    });
  };

  const idleItems = items
    .filter((item) => item.hydrateOn !== 'scroll')
    .sort((left, right) => left.hydrationPriority - right.hydrationPriority);

  scheduleIdleWork(() => {
    for (const item of idleItems) {
      enqueue(item);
    }
  });

  for (const item of items) {
    if (item.hydrateOn !== 'scroll') {
      continue;
    }
    enqueueWhenNearViewport(item, () => enqueue(item));
  }
}

/**
 * Registers document-ready hydration for an SSR client entry.
 *
 * @param loadMode - Hydration strategy for this client entry.
 */
export function registerHydrateSsrComponentList(loadMode: HydrateLoadMode) {
  const run = () => hydrateSsrComponentList(loadMode);

  if (import.meta.env.SSR) {
    run();
  } else if (document.readyState === 'complete' || document.readyState === 'interactive') {
    run();
  } else {
    document.addEventListener('DOMContentLoaded', run);
  }

  window.reHydrateComponents = run;
}
